import Script from 'next/script';
import { useCallback, useContext, useEffect, useState, createContext } from 'react';

import { AuthService } from '../services/AuthService';
import type { AuthData, AuthProviderProps } from '../types';

const AuthContext = createContext<AuthContextType>({
  auth: { user: null, apiToken: '', status: 'checking' },
  setStatusSignIn: () => {},
  setStatusSignOut: () => {},
  setStatusSignChecking: () => {},
  setIsAllowAuthGuard: () => {},
  isAllowAuthGuard: true,
  isUseCI: true,
  resetIsAllowAuthGuard: () => {}
});

export const AuthProvider = ({ children, appId, clientId, signPaths, navigationEvent, isUseCI = true }: AuthProviderProps) => {
  const [authService] = useState<AuthService>(() => new AuthService({ appId, clientId, signPaths }));
  const [accessToken, setAccessToken] = useState<string>(authService.accessToken);
  const [authStatus, setAuthStatus] = useState<'login' | 'logout' | 'checking'>('checking');
  const [isAllowAuthGuard, setIsAllowAuthGuard] = useState(true);

  const setStatusSignIn = () => {
    if (!authService.userData) return;
    setAccessToken(authService.accessToken);
    setAuthStatus('login');
    setIsAllowAuthGuard(true);
  };

  const setStatusSignOut = useCallback(
    (isBlockGuardAction: boolean = false) => {
      authService.removeAuthData();
      setAccessToken('');
      setAuthStatus('logout');
      setIsAllowAuthGuard(!isBlockGuardAction);
    },
    [authService]
  );

  const setStatusSignChecking = () => {
    authService.removeAuthData();
    setAccessToken('');
    setAuthStatus('checking');
    setIsAllowAuthGuard(true);
  };

  const resetIsAllowAuthGuard = useCallback(
    (timeout = 2000) => {
      if (isAllowAuthGuard) return;
      setTimeout(() => {
        setIsAllowAuthGuard(true);
      }, timeout);
    },
    [isAllowAuthGuard]
  );

  useEffect(() => {
    const clickHandler = () => {
      if (authService.accessToken) {
        authService.refreshAuthCookies();
      } else if (authStatus === 'login') {
        setStatusSignOut();
      }
    };

    navigationEvent?.registerNavigationEvent(resetIsAllowAuthGuard);
    window.addEventListener('click', clickHandler);
    return () => {
      navigationEvent?.removeNavigationEvent(resetIsAllowAuthGuard);
      window.removeEventListener('click', clickHandler);
    };
  }, [authService, isAllowAuthGuard, navigationEvent, resetIsAllowAuthGuard, setStatusSignOut, authStatus]);

  useEffect(() => {
    // NOTE: PageNavigationEventContext에서 가져온 navigationEvent가 없을 경우에 수행하는 대체액션입니다.(일부 액션이 비정상적으로 동작할 수 있습니다.)
    if (navigationEvent || isAllowAuthGuard) return;
    setTimeout(() => {
      setIsAllowAuthGuard(true);
    }, 2000);
  }, [isAllowAuthGuard, navigationEvent]);

  return (
    <>
      <AuthContext.Provider
        value={{
          auth: { user: authService.userData, apiToken: accessToken, status: authStatus },
          setStatusSignChecking,
          authService,
          setStatusSignIn,
          setStatusSignOut,
          isAllowAuthGuard,
          setIsAllowAuthGuard,
          isUseCI,
          resetIsAllowAuthGuard
        }}
      >
        {children}
      </AuthContext.Provider>
      <Script
        type="text/javascript"
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/ko_KR/appleid.auth.js"
        crossOrigin={'anonymous'}
        strategy={'beforeInteractive'}
      />
    </>
  );
};

export const useAuthContext = (): AuthContextType => {
  return useContext(AuthContext);
};

type AuthContextType = {
  auth: AuthData;
  setStatusSignIn: () => void;
  setStatusSignOut: (isBlockGuardAction?: boolean) => void;
  setStatusSignChecking: () => void;
  setIsAllowAuthGuard: (value: boolean) => void;
  isAllowAuthGuard: boolean;
  isUseCI: boolean;
  authService?: AuthService | undefined;
  resetIsAllowAuthGuard: (timeout?: number) => void;
};
